import { useMemo } from "react";

import { useCorrugates } from "@/hooks/corrugates";
import { useMachineGroups } from "@/hooks/machine-groups";
import { useProductionGroups } from "@/hooks/production-groups";

import {
	type ConfiguredCorrugates,
	type ConfiguredMachineGroups,
	ProductionGroupsTemplate,
} from "./production-groups.template";

const deletedZfoldLabel = "Deleted z-Fold";

export const ProductionGroups = () => {
	const { productionGroups, isLoading, error, refresh } = useProductionGroups({ refetchInterval: false });
	const { machineGroups } = useMachineGroups({ refetchInterval: false });
	const { corrugates } = useCorrugates({ refetchInterval: false });

	const machineGroupsList = useMemo(() => machineGroups ?? [], [machineGroups]);
	const corrugatesList = useMemo(() => corrugates ?? [], [corrugates]);
	const list = useMemo(() => productionGroups ?? [], [productionGroups]);

	const configuredMachineGroups = useMemo<ConfiguredMachineGroups>(() => {
		if (machineGroupsList.length === 0) return {};

		return list.reduce<ConfiguredMachineGroups>((acc, productionGroup) => {
			if (productionGroup.configuredMachineGroups.length === 0) return acc;

			const names = productionGroup.configuredMachineGroups.map((id) => {
				const machineGroup = machineGroupsList.find((mg) => mg.id === id);
				return machineGroup ? machineGroup.alias : "";
			});

			acc[productionGroup.id] = names.filter(Boolean).sort().join(", ");
			return acc;
		}, {});
	}, [list, machineGroupsList]);

	const configuredCorrugates = useMemo<ConfiguredCorrugates>(() => {
		if (corrugatesList.length === 0) return {};

		return list.reduce<ConfiguredCorrugates>((acc, productionGroup) => {
			if (!productionGroup.configuredCorrugates || productionGroup.configuredCorrugates.length === 0) return acc;

			const names = productionGroup.configuredCorrugates.map((id) => {
				const corrugate = corrugatesList.find((c) => c.id === id);
				return corrugate ? corrugate.alias : deletedZfoldLabel;
			});

			acc[productionGroup.id] = names.sort();
			return acc;
		}, {});
	}, [list, corrugatesList]);

	return (
		<ProductionGroupsTemplate
			productionGroups={list}
			configuredMachineGroups={configuredMachineGroups}
			configuredCorrugates={configuredCorrugates}
			machineGroups={machineGroupsList}
			corrugates={corrugatesList}
			isLoading={isLoading}
			isError={Boolean(error)}
			onRetry={refresh}
		/>
	);
};
